/**
 * Publishing, and the version history it produces (CLAUDE.md 21.3).
 *
 * The wording here is doing real work. Publishing is not "saving" -- the sheet is
 * already saved -- it is *issuing*: freezing what the calculation says so a link can
 * be handed to someone and keep meaning the same thing. The control says so, because
 * an author who thinks publishing is a save will not understand why editing
 * afterwards does not update what their colleague sees.
 */
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Check, History, Send } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Field, FieldLabel } from '@/components/ui/field'
import { FieldInput } from '@/components/ui/input'
import { formatRelative } from '@/lib/format'
import { listVersions, publishSheet } from '../api'

export function PublishControl({
  sheetId,
  publishedVersion,
  hasUnpublishedChanges,
  canEdit,
  onPublished,
  onBeforePublish,
}: {
  sheetId: number
  publishedVersion: number | null
  hasUnpublishedChanges: boolean
  canEdit: boolean
  onPublished: () => void
  /** Flushes any debounced autosave and resolves when it has landed. Publishing
   *  copies the document the *server* holds, so issuing before the save arrives
   *  would freeze the previous content. */
  onBeforePublish: () => Promise<void>
}) {
  const { t, i18n } = useTranslation('sheets')
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const [historyOpen, setHistoryOpen] = useState(false)
  const [note, setNote] = useState('')

  const versions = useQuery({
    queryKey: ['sheet', sheetId, 'versions'],
    queryFn: ({ signal }) => listVersions(sheetId, signal),
    enabled: historyOpen,
  })

  const publish = useMutation({
    mutationFn: async () => {
      await onBeforePublish()
      return publishSheet(sheetId, note.trim() ? { note: note.trim() } : {})
    },
    onSuccess: () => {
      setOpen(false)
      setNote('')
      void queryClient.invalidateQueries({ queryKey: ['sheet', sheetId] })
      void queryClient.invalidateQueries({ queryKey: ['sheets'] })
      onPublished()
    },
  })

  return (
    <>
      <div className="flex shrink-0 items-center gap-1">
        {publishedVersion !== null && (
          <button
            type="button"
            onClick={() => setHistoryOpen(true)}
            className="inline-flex h-9 items-center gap-1.5 rounded-md px-2 text-xs text-text-muted hover:text-text"
            data-testid="version-badge"
          >
            <History className="h-3.5 w-3.5" aria-hidden />
            {t('sheets.publish.version', { n: publishedVersion })}
          </button>
        )}

        {canEdit && (
          <Button
            variant={hasUnpublishedChanges ? 'primary' : 'ghost'}
            size="sm"
            onClick={() => setOpen(true)}
            data-testid="publish-button"
          >
            {hasUnpublishedChanges ? (
              <Send className="h-3.5 w-3.5" aria-hidden />
            ) : (
              <Check className="h-3.5 w-3.5" aria-hidden />
            )}
            {hasUnpublishedChanges ? t('sheets.publish.action') : t('sheets.publish.up_to_date')}
          </Button>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {publishedVersion === null
                ? t('sheets.publish.title_first')
                : t('sheets.publish.title_again', { n: publishedVersion + 1 })}
            </DialogTitle>
          </DialogHeader>

          <p className="text-sm leading-relaxed text-text-muted">{t('sheets.publish.explainer')}</p>

          <Field>
            <FieldLabel>{t('sheets.publish.note')}</FieldLabel>
            <FieldInput
              value={note}
              onChange={(event) => setNote(event.target.value)}
              placeholder={t('sheets.publish.note_placeholder')}
            />
          </Field>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              {t('sheets.publish.cancel')}
            </Button>
            <Button onClick={() => publish.mutate()} loading={publish.isPending}>
              {t('sheets.publish.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('sheets.publish.history')}</DialogTitle>
          </DialogHeader>

          <ul className="max-h-80 overflow-y-auto">
            {(versions.data ?? []).map((entry) => (
              <li
                key={entry.id}
                className="flex items-baseline gap-3 border-b border-border py-2 last:border-b-0"
              >
                <span className="tabular w-14 shrink-0 font-mono text-xs text-text-muted">
                  {t('sheets.publish.version', { n: entry.version })}
                </span>
                <span className="min-w-0 flex-1 text-sm">
                  {entry.note || t('sheets.publish.no_note')}
                </span>
                <span className="shrink-0 text-xs text-text-muted">
                  {formatRelative(entry.published_at, i18n.language)}
                </span>
              </li>
            ))}
          </ul>

          {/* The rule the history exists to make visible. */}
          <p className="mt-2 text-xs leading-relaxed text-text-muted">
            {t('sheets.publish.immutable_note')}
          </p>
        </DialogContent>
      </Dialog>
    </>
  )
}
